import { useState, useEffect, useCallback } from "react";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";

// ─── shared icon primitive ────────────────────────────────────────────────────
const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

const ICONS = {
  users:     "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  globe:     "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  userPlus:  "M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z",
  check:     "M5 13l4 4L19 7",
  search:    "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  x:         "M6 18L18 6M6 6l12 12",
  phone:     "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  mail:      "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  assign:    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  hash:      "M7 20l4-16m2 16l4-16M6 9h14M4 15h14",
};

const PALETTE = [
  "bg-blue-600","bg-emerald-600","bg-violet-600","bg-indigo-600",
  "bg-amber-500","bg-teal-600","bg-rose-600","bg-cyan-600",
  "bg-orange-500","bg-purple-600",
];

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-slate-50 focus:bg-white";

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function AssignUsers() {
  const [regions, setRegions]                 = useState([]);
  const [unassignedUsers, setUnassignedUsers]   = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState("");
  const [submitting, setSubmitting]           = useState(false);
  const [search, setSearch]                   = useState("");

  const [assignmentData, setAssignmentData]   = useState({ user_id: "", region_id: "" });

  const fetchData = useCallback(async () => {
    try {
      const [rr, ur] = await Promise.all([
        apiRequest(API_URLS.admin.getRegions),
        apiRequest(API_URLS.admin.unassignedUsers),
      ]);
      setRegions(rr.data || []);
      setUnassignedUsers(ur.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (key) => (e) => setAssignmentData(d => ({ ...d, [key]: e.target.value }));

  const handleAssign = async () => {
    if (!assignmentData.user_id || !assignmentData.region_id) {
      setError("Please select both a user and a region"); return;
    }
    setSubmitting(true);
    try {
      await apiRequest(API_URLS.admin.assignUser, "POST", assignmentData);
      setAssignmentData({ user_id: "", region_id: "" });
      fetchData();
    } catch (err) { setError(err.message || "Failed to assign user"); }
    finally { setSubmitting(false); }
  };

  const filteredUsers = unassignedUsers.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const SubmitBtn = ({ label, loadingLabel, onClick }) => (
    <button onClick={onClick} disabled={submitting}
      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors">
      {submitting
        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        : <Icon d={ICONS.check} className="w-4 h-4" />}
      {submitting ? loadingLabel : label}
    </button>
  );

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col items-center justify-center h-72 gap-4">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
        <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
      </div>
      <p className="text-sm text-slate-400 tracking-wide font-medium">Loading assignments…</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Assign Users</h1>
            <p className="text-xs text-slate-400 mt-0.5">Assign enumerators to specific regions</p>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* ── Summary strip ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Regions", value: regions.length, color: "bg-blue-600", icon: ICONS.globe },
            { label: "Unassigned Users", value: unassignedUsers.length, color: "bg-amber-500", icon: ICONS.users },
            { label: "Total Assigned", value: regions.reduce((s, r) => s + (r.users?.length || 0), 0), color: "bg-emerald-600", icon: ICONS.assign },
            { label: "Showing", value: filteredUsers.length, color: "bg-slate-400", icon: ICONS.hash },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-9 h-9 ${c.color} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon d={c.icon} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900 leading-none">{c.value}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Assignment Form ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                <Icon d={ICONS.assign} className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-800">New Assignment</h2>
                <p className="text-xs text-slate-400">Assign an enumerator to a region</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <Field label="Select Enumerator" required>
              <div className="relative">
                <select value={assignmentData.user_id} onChange={set("user_id")}
                  className={inputCls + " appearance-none pr-9"} required>
                  <option value="">Select an enumerator</option>
                  {unassignedUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} {user.work_number && `(${user.work_number})`}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </Field>

            <Field label="Select Region" required>
              <div className="relative">
                <select value={assignmentData.region_id} onChange={set("region_id")}
                  className={inputCls + " appearance-none pr-9"} required>
                  <option value="">Select a region</option>
                  {regions.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </Field>

            <SubmitBtn label="Assign User" loadingLabel="Assigning…" onClick={handleAssign} />
          </div>
        </div>

        {/* ── Unassigned Users ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-800">Unassigned Enumerators</h2>
              <p className="text-xs text-slate-400 mt-0.5">{filteredUsers.length} of {unassignedUsers.length} users</p>
            </div>
            <div className="relative max-w-xs w-full sm:w-auto">
              <Icon d={ICONS.search} className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search users…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <Icon d={ICONS.x} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon d={ICONS.users} className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium">
                {search ? `No users match "${search}"` : "All enumerators have been assigned"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredUsers.map((user, i) => {
                const dotColor = PALETTE[i % PALETTE.length];
                const initials = user.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";
                return (
                  <div key={user.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/60 transition-colors">
                    <div className={`w-8 h-8 ${dotColor} rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{user.name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {user.email && (
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Icon d={ICONS.mail} className="w-3 h-3" />
                            <span className="truncate">{user.email}</span>
                          </div>
                        )}
                        {user.work_number && (
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Icon d={ICONS.phone} className="w-3 h-3" />
                            <span>{user.work_number}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="px-2.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                      Unassigned
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Regions Overview ─────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Icon d={ICONS.globe} className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-800">Regions Overview</h2>
                <p className="text-xs text-slate-400">Current assignments by region</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {regions.map((region, i) => {
              const dotColor = PALETTE[i % PALETTE.length];
              const userCount = region.users?.length || 0;
              return (
                <div key={region.id} className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${dotColor} rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                        {region.name?.charAt(0).toUpperCase() || "R"}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-800">{region.name}</h3>
                    </div>
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${
                      userCount > 0
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}>
                      {userCount} {userCount === 1 ? "user" : "users"}
                    </span>
                  </div>
                  {region.users && region.users.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {region.users.map((user) => (
                        <span 
                          key={user.id}
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-full"
                        >
                          <Icon d={ICONS.userPlus} className="w-3 h-3" />
                          {user.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {(!region.users || region.users.length === 0) && (
                    <p className="text-xs text-slate-400 italic">No users assigned to this region</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
